// Grouping of a shooter's own Megalink shots for the profile «Skudd» tab:
// program (stage type) → series type (150s, 5x3s, …) → Lag. Pure, no DOM.
// Input: the rows of GET /api/megalinkshots/mine (lib/megalink-history.js).
// `labelsFor(type)` answers the series label per target index for a program —
// the component passes conventionResultStore.getParticipantFields.

import { targetFor } from './pistolTargets';
import { withSplits } from './shooterShots';

export const PERIOD_DAYS = [30, 365, 0]; // 0 = all
export const SIGHTER_LABEL = 'Prøve';

function timeOf(shot) {
    const ms = shot && shot.shotAtServer ? Date.parse(shot.shotAtServer) : NaN;
    return Number.isFinite(ms) ? ms : 0;
}

function lagTime(lag) {
    const ms = lag && lag.stage && lag.stage.date ? Date.parse(lag.stage.date) : NaN;
    return Number.isFinite(ms) ? ms : 0;
}

export function seriesLabel(labels, k) {
    const list = Array.isArray(labels) ? labels : [];
    return list[k] || `Serie ${k + 1}`;
}

// Series types of a program in program order, sighters first, each label once.
function seriesOrder(type, labelsFor) {
    const labels = typeof labelsFor === 'function' ? labelsFor(type) : [];
    const order = [SIGHTER_LABEL];
    (Array.isArray(labels) ? labels : []).forEach(label => {
        if (!order.includes(label)) order.push(label);
    });
    return order;
}

function seriesOf(shot, labels) {
    if (shot.countStatus === 'sighter') {
        return { label: SIGHTER_LABEL, targetIndex: 0, sighter: true };
    }
    const k = Number.isInteger(shot.cellTargetIndex) ? shot.cellTargetIndex : -1;
    if (k < 0) return null;
    return { label: seriesLabel(labels, k), targetIndex: k, sighter: false };
}

// A Lag row's shots per target (a series type like 150s covers two targets, and
// the pause between them must never read as a split): [{ targetIndex, shots, duration }]
// with shots[].split / duration from lib/shooterShots.js withSplits. Time order kept.
export function lagSeriesGroups(sortedShots) {
    const groups = [];
    (Array.isArray(sortedShots) ? sortedShots : []).forEach(shot => {
        const targetIndex = shot.countStatus === 'sighter' ? -1 : shot.cellTargetIndex;
        let group = groups.find(g => g.targetIndex === targetIndex);
        if (!group) {
            group = { targetIndex, shots: [] };
            groups.push(group);
        }
        group.shots.push(shot);
    });
    return groups.map(g => ({ targetIndex: g.targetIndex, ...withSplits(g.shots) }));
}

// [{ type, count, series: [{ key, label, sighter, target, count, shots, lags }] }]
// lags[].groups = lagSeriesGroups(lags[].shots) for the split-time chip line.
// Programs by shot count (most shot first); series in program order; a series'
// shots oldest first (the newest is drawn red); its Lag rows newest first.
export function buildShotHistory(lags, labelsFor) {
    const programs = new Map();
    (Array.isArray(lags) ? lags : []).forEach(lag => {
        if (!lag || !lag.stage || !Array.isArray(lag.shots)) return;
        const type = (lag.stage.type || '').toLowerCase();
        const labels = typeof labelsFor === 'function' ? labelsFor(type) : [];
        if (!programs.has(type)) programs.set(type, { type, count: 0, series: new Map() });
        const program = programs.get(type);
        const perSeries = new Map();
        lag.shots.forEach(shot => {
            const series = shot ? seriesOf(shot, labels) : null;
            if (!series) return;
            const key = `${type}|${series.label}`;
            if (!program.series.has(key)) {
                program.series.set(key, {
                    key,
                    label: series.label,
                    sighter: series.sighter,
                    target: targetFor(type, series.targetIndex),
                    count: 0,
                    shots: [],
                    lags: [],
                });
            }
            if (!perSeries.has(key)) perSeries.set(key, []);
            perSeries.get(key).push(shot);
        });
        perSeries.forEach((shots, key) => {
            const entry = program.series.get(key);
            const sorted = [...shots].sort((a, b) => timeOf(a) - timeOf(b));
            entry.shots = entry.shots.concat(sorted);
            entry.count += sorted.length;
            entry.lags.push({
                conventionId: lag.convention ? lag.convention.id : 0,
                title: (lag.convention && lag.convention.title) || '',
                isCompetition: !!(lag.convention && lag.convention.isCompetition),
                stageName: lag.stage.name || '',
                stageIdx: Number.isInteger(lag.stage.idx) ? lag.stage.idx : 0,
                date: lag.stage.date || null,
                count: sorted.length,
                shots: sorted,
                groups: lagSeriesGroups(sorted),
            });
            program.count += sorted.length;
        });
    });
    return [...programs.values()]
        .map(program => {
            const order = seriesOrder(program.type, labelsFor);
            const rank = s => {
                const i = order.indexOf(s.label);
                return i < 0 ? order.length : i;
            };
            const series = [...program.series.values()]
                .map(s => ({
                    ...s,
                    shots: [...s.shots].sort((a, b) => timeOf(a) - timeOf(b)),
                    lags: [...s.lags].sort((a, b) => Date.parse(b.date || 0) - Date.parse(a.date || 0)),
                }))
                .sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
            return { type: program.type, count: program.count, series };
        })
        .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
}

// Lag rows newer than `days` days before `now`; 0 = every row.
export function lagsWithinDays(lags, days, now = Date.now()) {
    const list = Array.isArray(lags) ? lags : [];
    if (!(days > 0)) return list;
    const from = now - days * 24 * 60 * 60 * 1000;
    return list.filter(lag => lagTime(lag) >= from);
}
